import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import usePageMetadata from '../utils/usePageMetadata';
import RevvyAdminTab from '../components/admin/RevvyAdminTab';
// InsightRulesManager removed - insights engine removed
import './AdminDashboard.css';

const DEFAULT_PLAN_OPTIONS = [
  {
    name: 'free',
    label: 'Free Plan',
    description: 'Default free subscription tier',
    dataRetentionDays: 31,
    minRefreshIntervalHours: 6,
    monthlyPrice: 0,
    yearlyPrice: 0,
    currency: 'usd',
    maxSites: 1,
    stripeProductId: null,
    stripeMonthlyPriceId: null,
    stripeYearlyPriceId: null
  },
  {
    name: 'basic',
    label: 'Basic Plan',
    description: 'Faster refresh times and extended data retention',
    dataRetentionDays: 90,
    minRefreshIntervalHours: 3,
    monthlyPrice: 49,
    yearlyPrice: 499,
    currency: 'usd',
    maxSites: 3,
    stripeProductId: null,
    stripeMonthlyPriceId: null,
    stripeYearlyPriceId: null
  },
  {
    name: 'pro',
    label: 'Pro Plan',
    description: 'Advanced refresh speeds with expanded retention for growing publishers',
    dataRetentionDays: 180,
    minRefreshIntervalHours: 1,
    monthlyPrice: 99,
    yearlyPrice: 999,
    currency: 'usd',
    maxSites: 10,
    stripeProductId: null,
    stripeMonthlyPriceId: null,
    stripeYearlyPriceId: null
  },
  {
    name: 'ultimate',
    label: 'Ultimate Plan',
    description: 'Unlimited refresh frequency with standard retention window',
    dataRetentionDays: 365,
    minRefreshIntervalHours: 0,
    monthlyPrice: 199,
    yearlyPrice: 1999,
    currency: 'usd',
    maxSites: 999,
    stripeProductId: null,
    stripeMonthlyPriceId: null,
    stripeYearlyPriceId: null
  },
  {
    name: 'enterprise',
    label: 'Enterprise',
    description: 'Custom SLAs, dedicated onboarding, and white-glove support for scaled publishers',
    dataRetentionDays: null,
    minRefreshIntervalHours: null,
    monthlyPrice: null,
    yearlyPrice: null,
    currency: 'usd',
    maxSites: null,
    stripeProductId: null,
    stripeMonthlyPriceId: null,
    stripeYearlyPriceId: null
  }
];

const combineAnalyticsResults = (results) => {
  if (!results || results.length === 0) {
    return { error: 'No results to combine' };
  }

  if (results.length === 1) {
    return results[0];
  }

  const hasError = results.some(r => r.error);
  if (hasError) {
    return { error: 'One or more requests failed' };
  }

  const combined = {
    summary: {},
    deviceBreakdown: {
      desktop: { users: 0, percentage: 0 },
      mobile: { users: 0, percentage: 0 },
      tablet: { users: 0, percentage: 0 }
    },
    dailyData: []
  };

  const sumMetrics = ['users', 'sessions', 'pageviews', 'engagedSessions', 'newUsers', 'returningUsers'];
  const sumKeys = ['totalUsers', 'totalSessions', 'totalPageviews', 'totalEngagedSessions', 'totalNewUsers', 'totalReturningUsers'];

  results.forEach(result => {
    if (!result.summary) return;

    sumKeys.forEach((key, idx) => {
      const metricName = sumMetrics[idx];
      if (result.summary[metricName]?.value !== undefined) {
        if (!combined.summary[metricName]) {
          combined.summary[metricName] = { value: 0 };
        }
        combined.summary[metricName].value += result.summary[metricName].value;
      }
    });

    if (result.deviceBreakdown) {
      ['desktop', 'mobile', 'tablet'].forEach(device => {
        if (result.deviceBreakdown[device]?.users) {
          combined.deviceBreakdown[device].users += result.deviceBreakdown[device].users;
        }
      });
    }
  });

  const totalDeviceUsers = combined.deviceBreakdown.desktop.users +
                          combined.deviceBreakdown.mobile.users +
                          combined.deviceBreakdown.tablet.users;

  if (totalDeviceUsers > 0) {
    combined.deviceBreakdown.desktop.percentage = Math.round((combined.deviceBreakdown.desktop.users / totalDeviceUsers) * 100);
    combined.deviceBreakdown.mobile.percentage = Math.round((combined.deviceBreakdown.mobile.users / totalDeviceUsers) * 100);
    combined.deviceBreakdown.tablet.percentage = Math.round((combined.deviceBreakdown.tablet.users / totalDeviceUsers) * 100);
  } else {
    combined.deviceBreakdown.desktop.percentage = 0;
    combined.deviceBreakdown.mobile.percentage = 0;
    combined.deviceBreakdown.tablet.percentage = 0;
  }

  const totalSessions = combined.summary.sessions?.value || 0;
  const totalEngagedSessions = combined.summary.engagedSessions?.value || 0;

  if (totalSessions > 0) {
    combined.summary.engagementRate = { value: Math.round((totalEngagedSessions / totalSessions) * 100 * 100) / 100 };

    let totalBounceSessions = 0;
    let totalDuration = 0;
    let totalSessionCount = 0;

    results.forEach(result => {
      if (result.summary?.sessions?.value) {
        totalSessionCount += result.summary.sessions.value;
        if (result.summary.bounceRate?.value) {
          totalBounceSessions += (result.summary.sessions.value * result.summary.bounceRate.value / 100);
        }
        if (result.summary.avgSessionDuration?.value) {
          totalDuration += (result.summary.avgSessionDuration.value * result.summary.sessions.value);
        }
      }
    });

    if (totalSessionCount > 0) {
      combined.summary.bounceRate = { value: Math.round((totalBounceSessions / totalSessionCount) * 100 * 100) / 100 };
      combined.summary.avgSessionDuration = { value: Math.round(totalDuration / totalSessionCount) };
    }
  }

  if (combined.summary.users?.value) {
    combined.summary.usersPerSession = { 
      value: Math.round((combined.summary.sessions?.value || 0) / combined.summary.users.value * 100) / 100 
    };
    combined.summary.pageviewsPerUser = { 
      value: Math.round((combined.summary.pageviews?.value || 0) / combined.summary.users.value * 100) / 100 
    };
  }

  results.forEach(result => {
    if (result.summary && !combined.summary.changeAdded) {
      ['users', 'sessions', 'pageviews'].forEach(key => {
        if (result.summary[key]?.change !== undefined) {
          if (!combined.summary[key]) combined.summary[key] = {};
          combined.summary[key].change = result.summary[key].change;
        }
      });
      combined.summary.changeAdded = true;
    }
  });

  return combined;
};

const AdminDashboard = () => {
  usePageMetadata({
    title: 'Admin Dashboard | The Ad Shop',
    description: 'Administrative dashboard for managing users, subscription plans, and system settings.'
  });

  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [stats, setStats] = useState({});
  const [plans, setPlans] = useState(DEFAULT_PLAN_OPTIONS);
  const [plansLoading, setPlansLoading] = useState(false);
  const [planError, setPlanError] = useState('');
  const [selectedPlanName, setSelectedPlanName] = useState('free');
  const [planForm, setPlanForm] = useState({
    name: 'free',
    label: 'Free Plan',
    description: '',
    dataRetentionDays: 31,
    minRefreshIntervalHours: 6,
    monthlyPrice: 0,
    yearlyPrice: 0,
    currency: 'usd',
    maxSites: 1,
    stripeProductId: null,
    stripeMonthlyPriceId: null,
    stripeYearlyPriceId: null
  });
  const [planSyncing, setPlanSyncing] = useState(false);
  const [planSyncStatus, setPlanSyncStatus] = useState(null);
  const [planEditUserId, setPlanEditUserId] = useState(null);
  const [planEditValue, setPlanEditValue] = useState('free');
  const [planSavingUserId, setPlanSavingUserId] = useState(null);
  const [cancelingUserId, setCancelingUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [settings, setSettings] = useState({
    requireUserApproval: true,
    emailVerificationRequired: true
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  const planLabelMap = useMemo(() => {
    const map = {};
    plans.forEach(plan => {
      map[plan.name] = plan.label || plan.name;
    });
    return map;
  }, [plans]);

  const planCountsEntries = useMemo(() => {
    if (!stats?.planCounts) {
      return [];
    }

    return Object.entries(stats.planCounts).map(([name, count]) => ({
      name,
      label: planLabelMap[name] || name,
      count
    })).sort((a, b) => a.label.localeCompare(b.label));
  }, [planLabelMap, stats.planCounts]);
  
  // API Testing state
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [baseApiResult, setBaseApiResult] = useState(null);
  const [apiResult, setApiResult] = useState(null);
  const [apiLoading, setApiLoading] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({ device: 'all', webTrafficSources: [], socialSources: [], aiSources: [], emailSelected: false, geo: 'all' });
  const [availableGeos, setAvailableGeos] = useState([]);
  
  // AdSense API Testing state
  const [adsenseApiResult, setAdsenseApiResult] = useState(null);
  const [adsenseApiLoading, setAdsenseApiLoading] = useState(false);

  const fetchSites = useCallback(async () => {
    try {
      const response = await axios.get('/api/sites');
      const sitesData = response.data.sites;
      setSites(sitesData);
      if (sitesData.length > 0) {
        setSelectedSite(sitesData[0]);
      }
    } catch (error) {
      console.error('Error fetching sites:', error);
    }
  }, []);

  const fetchBaseData = useCallback(async (site) => {
    if (!site) return;
    try {
      const params = new URLSearchParams({ days: 7 });
      const response = await axios.get(`/api/analytics-data/${site.siteId}/overview?${params}`);
      setBaseApiResult(response.data);
    } catch (error) {
      console.error('Error fetching base data:', error);
      setBaseApiResult({ error: error.response?.data?.message || 'Failed to fetch data' });
    }
  }, []);

  const fetchUsers = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString()
      });

      if (filters.status !== 'all') {
        params.append('status', filters.status);
      }
      if (filters.search) {
        params.append('search', filters.search);
      }

      const response = await axios.get(`/api/admin/users?${params}`);
      setUsers(response.data.users);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching users:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('token');
        navigate('/admin/login');
      } else {
        setError('Failed to fetch users');
      }
    } finally {
      setLoading(false);
    }
  }, [filters.search, filters.status, navigate, pagination.limit]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get('/api/admin/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  const fetchPlans = useCallback(async () => {
    try {
      setPlansLoading(true);
      const response = await axios.get('/api/admin/subscription-plans');
      const plansData = response.data?.plans || [];

      const planMap = new Map();
      DEFAULT_PLAN_OPTIONS.forEach(plan => {
        planMap.set(plan.name, { ...plan });
      });

      plansData.forEach(plan => {
        if (!plan || !plan.name) return;
        const defaultPlan = DEFAULT_PLAN_OPTIONS.find(p => p.name === plan.name) || {};
        planMap.set(plan.name, {
          name: plan.name,
          label: plan.label || plan.name,
          description: plan.description || defaultPlan.description || '',
          dataRetentionDays: plan.dataRetentionDays ?? defaultPlan.dataRetentionDays ?? 31,
          minRefreshIntervalHours: plan.minRefreshIntervalHours ?? defaultPlan.minRefreshIntervalHours ?? 0,
          monthlyPrice: plan.monthlyPrice ?? defaultPlan.monthlyPrice ?? 0,
          yearlyPrice: plan.yearlyPrice ?? defaultPlan.yearlyPrice ?? 0,
          currency: (plan.currency || defaultPlan.currency || 'usd').toLowerCase(),
          maxSites: plan.maxSites ?? defaultPlan.maxSites ?? 1,
          stripeProductId: plan.stripeProductId || null,
          stripeMonthlyPriceId: plan.stripeMonthlyPriceId || null,
          stripeYearlyPriceId: plan.stripeYearlyPriceId || null,
          isDefault: plan.isDefault ?? false
        });
      });

      const mergedPlans = Array.from(planMap.values());
      setPlans(mergedPlans);

      const preferredPlan =
        mergedPlans.find(plan => plan.name === selectedPlanName) || mergedPlans[0];

      if (preferredPlan) {
        setSelectedPlanName(preferredPlan.name);
        setPlanForm({
          name: preferredPlan.name,
          label: preferredPlan.label || preferredPlan.name,
          description: preferredPlan.description || '',
          dataRetentionDays: preferredPlan.dataRetentionDays,
          minRefreshIntervalHours: preferredPlan.minRefreshIntervalHours,
          monthlyPrice: preferredPlan.monthlyPrice,
          yearlyPrice: preferredPlan.yearlyPrice,
          currency: preferredPlan.currency,
          maxSites: preferredPlan.maxSites,
          stripeProductId: preferredPlan.stripeProductId,
          stripeMonthlyPriceId: preferredPlan.stripeMonthlyPriceId,
          stripeYearlyPriceId: preferredPlan.stripeYearlyPriceId
        });
      }

      setPlanError('');
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
      setPlanError(error.response?.data?.message || 'Failed to load subscription plans');
    } finally {
      setPlansLoading(false);
    }
  }, [selectedPlanName]);

  const fetchAdmins = useCallback(async () => {
    try {
      // Get all admins by fetching users with isAdmin filter
      const response = await axios.get('/api/admin/users?status=all');
      const allUsers = response.data.users;
      const adminsList = allUsers.filter(user => user.isAdmin === true || user.isAdmin === 'true');
      console.log('All users:', allUsers);
      console.log('Filtered admins:', adminsList);
      setAdmins(adminsList);
    } catch (error) {
      console.error('Error fetching admins:', error);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get('/api/admin/settings');
      if (response.data.settings) {
        setSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  }, []);

  const handlePromote = async (userId, action) => {
    try {
      await axios.post('/api/admin/promote', { userId, action });
      fetchAdmins(); // Refresh admins list
      fetchUsers(); // Refresh users list
      fetchStats(); // Refresh stats
      setError('');
    } catch (error) {
      console.error('Error promoting user:', error);
      setError(error.response?.data?.message || 'Failed to promote/demote user');
    }
  };

  const handleSettingsSave = async () => {
    try {
      setSettingsLoading(true);
      await axios.put('/api/admin/settings', settings);
      setError('');
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      setError('Failed to save settings');
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handlePlanSelect = (planName) => {
    setSelectedPlanName(planName);
    const plan = plans.find(p => p.name === planName);
    if (plan) {
      setPlanForm({
        name: plan.name,
        label: plan.label || plan.name,
        description: plan.description || '',
        dataRetentionDays: plan.dataRetentionDays ?? 31,
        minRefreshIntervalHours: plan.minRefreshIntervalHours ?? 6,
        monthlyPrice: plan.monthlyPrice ?? 0,
        yearlyPrice: plan.yearlyPrice ?? 0,
        currency: plan.currency || 'usd',
        maxSites: plan.maxSites ?? 1,
        stripeProductId: plan.stripeProductId || null,
        stripeMonthlyPriceId: plan.stripeMonthlyPriceId || null,
        stripeYearlyPriceId: plan.stripeYearlyPriceId || null
      });
      setPlanSyncStatus(null);
    }
  };

  const handlePlanInputChange = (field, value) => {
    setPlanForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePlanSave = async () => {
    try {
      setPlansLoading(true);
      setPlanError('');
      setPlanSyncStatus(null);

      const response = await axios.put('/api/admin/subscription-plans', {
        name: planForm.name,
        label: planForm.label,
        description: planForm.description,
        dataRetentionDays: Number(planForm.dataRetentionDays),
        minRefreshIntervalHours: Number(planForm.minRefreshIntervalHours),
        monthlyPrice: Number(planForm.monthlyPrice),
        yearlyPrice: Number(planForm.yearlyPrice),
        currency: planForm.currency,
        maxSites: Number(planForm.maxSites)
      });

      if (response.data?.plan) {
        const normalized = response.data.plan;
        setPlanForm(prev => ({
          ...prev,
          name: normalized.name,
          label: normalized.label,
          description: normalized.description,
          dataRetentionDays: normalized.dataRetentionDays,
          minRefreshIntervalHours: normalized.minRefreshIntervalHours,
          monthlyPrice: normalized.monthlyPrice,
          yearlyPrice: normalized.yearlyPrice,
          currency: normalized.currency,
          maxSites: normalized.maxSites,
          stripeProductId: normalized.stripeProductId,
          stripeMonthlyPriceId: normalized.stripeMonthlyPriceId,
          stripeYearlyPriceId: normalized.stripeYearlyPriceId
        }));
        setPlans((prevPlans) =>
          prevPlans.map((plan) =>
            plan.name === normalized.name
              ? { ...plan, ...normalized }
              : plan
          )
        );
      }

      await Promise.all([fetchPlans(), fetchStats()]);
      alert('Subscription plan updated successfully.');
    } catch (error) {
      console.error('Error saving plan settings:', error);
      setPlanError(error.response?.data?.message || 'Failed to save plan settings');
    } finally {
      setPlansLoading(false);
    }
  };

  const handlePlanSync = async () => {
    try {
      setPlanSyncing(true);
      setPlanSyncStatus(null);
      const response = await axios.post('/api/admin/stripe-sync', {
        planName: planForm.name
      });
      await Promise.all([fetchPlans(), fetchStats()]);
      setPlanSyncStatus({
        type: 'success',
        message: response.data?.message || 'Stripe pricing synchronized successfully.'
      });
    } catch (error) {
      console.error('Error syncing plan with Stripe:', error);
      setPlanSyncStatus({
        type: 'error',
        message: error.response?.data?.message || 'Failed to sync pricing with Stripe.'
      });
    } finally {
      setPlanSyncing(false);
    }
  };

  const handlePlanBadgeClick = (user) => {
    if (!plans || plans.length === 0) return;
    setPlanError('');
    setPlanEditUserId(user._id);
    setPlanEditValue(user.plan || 'free');
  };

  const handlePlanBadgeKeyDown = (event, user) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handlePlanBadgeClick(user);
    }
  };

  const handleUserPlanChange = useCallback(async (userId, newPlan) => {
    if (!newPlan) return;
    setPlanSavingUserId(userId);
    setPlanError('');
    try {
      const response = await axios.put(`/api/admin/users/${userId}/plan`, { plan: newPlan });
      const updatedPlanName = response.data?.plan?.name || newPlan;
      setUsers(prev =>
        prev.map(user =>
          user._id === userId ? { ...user, plan: updatedPlanName } : user
        )
      );
      setPlanEditValue(updatedPlanName);
      setPlanEditUserId(null);
      setError('');
      await fetchStats();
    } catch (error) {
      console.error('Error updating user plan:', error);
      setError(error.response?.data?.message || 'Failed to update user plan');
    } finally {
      setPlanSavingUserId(null);
    }
  }, [fetchStats]);

  const handleCancelSubscription = useCallback(
    async (user) => {
      if (!user || !user._id) return;
      const confirmation = window.confirm(
        `Cancel subscription for ${user.firstName || ''} ${user.lastName || ''}? They will retain access until the end of the current billing period.`
      );
      if (!confirmation) return;

      try {
        setCancelingUserId(user._id);
        await axios.post(`/api/admin/users/${user._id}/cancel-subscription`);
        await fetchUsers(pagination.page);
        setError('');
      } catch (error) {
        console.error('Error cancelling subscription:', error);
        setError(error.response?.data?.message || 'Failed to cancel subscription');
      } finally {
        setCancelingUserId(null);
      }
    },
    [fetchUsers, pagination.page]
  );

  const handleApprove = async (userId) => {
    try {
      await axios.patch(`/api/admin/users/${userId}`, { action: 'approve' });
      setUsers(users.map(user => 
        user._id === userId 
          ? { ...user, status: 'approved', approvedAt: new Date() }
          : user
      ));
      fetchStats(); // Refresh stats
    } catch (error) {
      console.error('Error approving user:', error);
      setError('Failed to approve user');
    }
  };

  const handleReject = async (userId) => {
    try {
      await axios.patch(`/api/admin/users/${userId}`, { 
        action: 'reject', 
        rejectionReason 
      });
      setUsers(users.map(user => 
        user._id === userId 
          ? { ...user, status: 'rejected', rejectionReason }
          : user
      ));
      setShowRejectModal(false);
      setRejectionReason('');
      fetchStats(); // Refresh stats
    } catch (error) {
      console.error('Error rejecting user:', error);
      setError('Failed to reject user');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`/api/admin/users/${userId}`);
      setUsers(users.filter(user => user._id !== userId));
      fetchStats(); // Refresh stats
    } catch (error) {
      console.error('Error deleting user:', error);
      setError('Failed to delete user');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers(1);
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { class: 'status-pending', text: 'Pending' },
      approved: { class: 'status-approved', text: 'Approved' },
      rejected: { class: 'status-rejected', text: 'Rejected' }
    };
    const badge = badges[status];
    return <span className={`status-badge ${badge.class}`}>{badge.text}</span>;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const formatSubscriptionDate = (date) => {
    if (!date) return '—';
    try {
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return '—';
    }
  };
  
  const getAllMetricsWithSubmetrics = (summary, deviceBreakdown, filters = { device: 'all', trafficSource: 'all' }) => {
    const metrics = [];
    
    // Build filter context for formula display
    let filterContext = '';
    const filterParts = [];
    
    if (filters.device !== 'all') {
      filterParts.push(`device = '${filters.device}'`);
    }
    
    if (filters.trafficSource !== 'all') {
      filterParts.push(`trafficSource = '${filters.trafficSource}'`);
    }
    
    if (filters.geo && filters.geo !== 'all') {
      filterParts.push(`geo = '${filters.geo}'`);
    }
    
    if (filterParts.length > 0) {
      filterContext = ` WHERE ${filterParts.join(' AND ')}`;
    }
    
    // Main metrics from summary
    if (summary.users) {
      metrics.push({
        key: 'users',
        label: 'Users',
        value: summary.users.value?.toLocaleString() || 0,
        formula: `SUM(dailyData.users${filterContext})`
      });
      metrics.push({
        key: 'newUsers',
        label: '  └ New Users',
        value: summary.newUsers?.value?.toLocaleString() || 0,
        formula: `SUM(dailyData.newUsers${filterContext})`
      });
      metrics.push({
        key: 'returnUsers',
        label: '  └ Return Users',
        value: summary.returnUsers?.value?.toLocaleString() || 0,
        formula: `SUM(dailyData.returnUsers${filterContext})`
      });
    }
    
    if (summary.sessions) {
      metrics.push({
        key: 'sessions',
        label: 'Sessions',
        value: summary.sessions.value?.toLocaleString() || 0,
        formula: `SUM(dailyData.sessions${filterContext})`
      });
      metrics.push({
        key: 'usersPerSession',
        label: '  └ Users per Session',
        value: summary.usersPerSession?.value?.toFixed(2) || '0',
        formula: `SUM(sessions${filterContext}) / SUM(users${filterContext})`
      });
    }
    
    if (summary.pageviews) {
      metrics.push({
        key: 'pageviews',
        label: 'Pageviews',
        value: summary.pageviews.value?.toLocaleString() || 0,
        formula: `SUM(dailyData.pageviews${filterContext})`
      });
      metrics.push({
        key: 'pageviewsPerUser',
        label: '  └ Pageviews per User',
        value: summary.pageviewsPerUser?.value?.toFixed(2) || '0',
        formula: `SUM(pageviews${filterContext}) / SUM(users${filterContext})`
      });
    }
    
    if (summary.engagementRate) {
      metrics.push({
        key: 'engagementRate',
        label: 'Engagement Rate',
        value: summary.engagementRate.value?.toFixed(1) + '%' || '0%',
        formula: `(SUM(engagedSessions${filterContext}) / SUM(sessions${filterContext})) * 100`
      });
    }
    
    if (summary.avgEngagement) {
      metrics.push({
        key: 'avgEngagement',
        label: 'Avg. Engagement',
        value: formatAvgEngagement(summary.avgEngagement.value),
        formula: `AVG(dailyData.avgSessionDuration${filterContext})`
      });
    }
    
    if (summary.bounceRate) {
      metrics.push({
        key: 'bounceRate',
        label: 'Bounce Rate',
        value: summary.bounceRate.value?.toFixed(1) + '%' || '0%',
        formula: `AVG(dailyData.bounceRate${filterContext})`
      });
    }
    
    // Device breakdown metrics
    if (deviceBreakdown) {
      metrics.push({
        key: 'desktop',
        label: 'Desktop',
        value: `${deviceBreakdown.desktop?.percentage || 0}%`,
        formula: `(SUM(desktopUsers${filterContext}) / SUM(totalUsers${filterContext})) * 100`
      });
      metrics.push({
        key: 'mobile',
        label: 'Mobile',
        value: `${deviceBreakdown.mobile?.percentage || 0}%`,
        formula: `(SUM(mobileUsers${filterContext}) / SUM(totalUsers${filterContext})) * 100`
      });
      metrics.push({
        key: 'tablet',
        label: 'Tablet',
        value: `${deviceBreakdown.tablet?.percentage || 0}%`,
        formula: `(SUM(tabletUsers${filterContext}) / SUM(totalUsers${filterContext})) * 100`
      });
    }
    
    // Geo breakdown metrics (from topGeos if available)
    // Note: This shows top geos, not the filtered geo metric
    // The filtered geo is already applied to all metrics above via filterContext
    
    return metrics;
  };
  
  const formatAvgEngagement = (seconds) => {
    if (!seconds) return '0s';
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    }
    return `${secs}s`;
  };
  
  const testGA4API = async () => {
    if (!selectedSite) {
      setError('Please select a site');
      return;
    }
    
    setApiLoading(true);
    
    try {
      // Fetch all data without filters
      const params = new URLSearchParams({ days: 7 });
      const response = await axios.get(`/api/analytics-data/${selectedSite.siteId}/overview?${params}`);
      
      // Store in base result and clear filtered result
      setBaseApiResult(response.data);
      setApiResult(null);
      // Extract available geos from topGeos
      if (response.data.topGeos && Array.isArray(response.data.topGeos)) {
        setAvailableGeos(response.data.topGeos.map(g => g.country));
      } else {
        setAvailableGeos([]);
      }
    } catch (error) {
      console.error('Error testing GA4 API:', error);
      setBaseApiResult({ error: error.response?.data?.message || 'Failed to fetch data' });
      setApiResult(null);
    } finally {
      setApiLoading(false);
    }
  };
  
  const handleApiFilterChange = (filterType, value) => {
    setSelectedFilters(prev => ({ ...prev, [filterType]: value }));
  };
  
  const fetchFilteredData = useCallback(async () => {
    if (!selectedSite) return;
    
    setApiLoading(true);
    
    try {
      // Collect all selected traffic sources
      const allTrafficSources = [];
      if (selectedFilters.webTrafficSources.length > 0) {
        allTrafficSources.push(...selectedFilters.webTrafficSources);
      }
      if (selectedFilters.socialSources.length > 0) {
        allTrafficSources.push(...selectedFilters.socialSources);
      }
      if (selectedFilters.aiSources.length > 0) {
        allTrafficSources.push(...selectedFilters.aiSources);
      }
      if (selectedFilters.emailSelected) {
        allTrafficSources.push('email');
      }
      
      // If no filters selected, clear filtered data
      if (allTrafficSources.length === 0 && selectedFilters.device === 'all' && (!selectedFilters.geo || selectedFilters.geo === 'all')) {
        setApiResult(null);
        setApiLoading(false);
        return;
      }
      
      // Fetch data for each selected traffic source (and device/geo if specified)
      const promises = [];
      
      // Add base request if no traffic sources selected but device/geo filters are active
      if (allTrafficSources.length === 0) {
        const params = new URLSearchParams({ days: 7 });
        if (selectedFilters.device !== 'all') {
          params.append('device', selectedFilters.device);
        }
        if (selectedFilters.geo && selectedFilters.geo !== 'all') {
          params.append('geo', selectedFilters.geo);
        }
        // Only make request if at least one filter is active
        if (selectedFilters.device !== 'all' || (selectedFilters.geo && selectedFilters.geo !== 'all')) {
          promises.push(axios.get(`/api/analytics-data/${selectedSite.siteId}/overview?${params}`));
        }
      }
      
      // Add requests for each traffic source (ALWAYS include device/geo filters if selected)
      allTrafficSources.forEach(trafficSource => {
        const params = new URLSearchParams({ days: 7 });
        params.append('trafficSource', trafficSource);
        // ALWAYS append device and geo if they're not 'all'
        if (selectedFilters.device !== 'all') {
          params.append('device', selectedFilters.device);
        }
        if (selectedFilters.geo && selectedFilters.geo !== 'all') {
          params.append('geo', selectedFilters.geo);
        }
        const url = `/api/analytics-data/${selectedSite.siteId}/overview?${params}`;
        console.log(`🔍 Fetching filtered data: ${url}`);
        promises.push(axios.get(url));
      });
      
      // Wait for all requests
      const responses = await Promise.all(promises);
      
      console.log(`✅ Received ${responses.length} responses for filtered data`);
      responses.forEach((response, idx) => {
        console.log(`Response ${idx + 1}:`, {
          url: response.config?.url,
          summary: response.data?.summary,
          hasData: !!response.data?.summary,
          users: response.data?.summary?.users?.value,
          sessions: response.data?.summary?.sessions?.value
        });
      });
      
      // Combine results
      if (responses.length === 0) {
        setApiResult({ error: 'No data to fetch' });
        return;
      }
      
      const combinedResult = combineAnalyticsResults(responses.map(r => r.data));
      console.log('📊 Combined result:', {
        hasSummary: !!combinedResult.summary,
        summaryKeys: combinedResult.summary ? Object.keys(combinedResult.summary) : [],
        users: combinedResult.summary?.users?.value,
        sessions: combinedResult.summary?.sessions?.value,
        pageviews: combinedResult.summary?.pageviews?.value
      });
      setApiResult(combinedResult);
    } catch (error) {
      console.error('Error fetching filtered data:', error);
      setApiResult({ error: error.response?.data?.message || 'Failed to fetch data' });
    } finally {
      setApiLoading(false);
    }
  }, [selectedFilters, selectedSite]);

  // Check if user is admin
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    // Set axios default header
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    
    fetchUsers();
    fetchStats();
    fetchAdmins();
    fetchSettings();
    fetchSites();
    fetchPlans();
  }, [fetchAdmins, fetchPlans, fetchSettings, fetchSites, fetchStats, fetchUsers, navigate]);
  useEffect(() => {
    if (activeTab === 'plans') {
      fetchPlans();
    }
  }, [activeTab, fetchPlans]);

  // Fetch base data when site is selected
  useEffect(() => {
    if (selectedSite) {
      fetchBaseData(selectedSite);
    }
  }, [fetchBaseData, selectedSite]);

  // Fetch filtered data when filters change
  useEffect(() => {
    if (baseApiResult && selectedSite) {
      fetchFilteredData();
    }
  }, [baseApiResult, fetchFilteredData, selectedFilters, selectedSite]);

  if (loading && users.length === 0) {
    return (
      <div className="admin-dashboard">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <h1>Admin Dashboard</h1>
        <p>Manage users, subscription plans, and system settings</p>
      </header>

      {error && (
        <div className="error-banner">
          {error}
          <button onClick={() => setError('')}>×</button>
        </div>
      )}

      {/* Tabs */}
      <div className="admin-tabs">
        <div className="tabs-container">
          <button 
            className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            User Management
          </button>
          <button 
            className={`tab-button ${activeTab === 'admins' ? 'active' : ''}`}
            onClick={() => setActiveTab('admins')}
          >
            Admin Users
          </button>
          <button 
            className={`tab-button ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            System Settings
          </button>
          <button 
            className={`tab-button ${activeTab === 'plans' ? 'active' : ''}`}
            onClick={() => setActiveTab('plans')}
          >
            Subscription Plans
          </button>
          <button 
            className={`tab-button ${activeTab === 'revvy' ? 'active' : ''}`}
            onClick={() => setActiveTab('revvy')}
          >
            Revvy
          </button>
          <button 
            className={`tab-button ${activeTab === 'api-test' ? 'active' : ''}`}
            onClick={() => setActiveTab('api-test')}
          >
            API Testing
          </button>
        </div>
      </div>

      {/* Stats Cards - only show for users tab */}
      {activeTab === 'users' && (
      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total Users</h3>
          <div className="stat-value">{stats.totalUsers || 0}</div>
        </div>
        <div className="stat-card pending">
          <h3>Pending Approval</h3>
          <div className="stat-value">{stats.pendingUsers || 0}</div>
        </div>
        <div className="stat-card approved">
          <h3>Approved</h3>
          <div className="stat-value">{stats.approvedUsers || 0}</div>
        </div>
        <div className="stat-card rejected">
          <h3>Rejected</h3>
          <div className="stat-value">{stats.rejectedUsers || 0}</div>
        </div>
      </div>
      )}

      {/* Filters - only show for users tab */}
      {activeTab === 'users' && (
      <div className="filters-section">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="Search users..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="search-input"
          />
          <button type="submit" className="search-btn">Search</button>
        </form>

        <div className="filter-controls">
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="status-filter"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>
      )}

      {/* Users Tab Content */}
      {activeTab === 'users' && (
        <>
      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Email Verified</th>
              <th>Role</th>
              <th>Plan</th>
              <th>Subscription Ends</th>
              <th>Website URL</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user._id}>
                <td>
                  <div className="user-info">
                    <div className="user-name">
                      {user.firstName} {user.lastName}
                    </div>
                  </div>
                </td>
                <td>{user.email}</td>
                <td>
                      {user.emailVerified ? (
                        <span className="status-badge status-approved">✓ Verified</span>
                      ) : (
                        <span className="status-badge" style={{ background: '#fef3c7', color: '#92400e' }}>✗ Not Verified</span>
                      )}
                    </td>
                    <td>
                      {user.isAdmin ? (
                        <span className="status-badge admin-badge" title="Administrator">
                          👑 Admin
                        </span>
                      ) : (
                        <span className="status-badge" style={{ background: '#f3f4f6', color: '#6b7280' }}>User</span>
                      )}
                    </td>
                  <td>
                    {planEditUserId === user._id ? (
                      <div className="plan-edit-inline">
                        <select
                          className="plan-edit-select"
                          value={planEditValue}
                          onChange={async (e) => {
                            const nextValue = e.target.value;
                            setPlanEditValue(nextValue);
                            await handleUserPlanChange(user._id, nextValue);
                          }}
                          onBlur={() => {
                            if (!planSavingUserId) {
                              setPlanEditUserId(null);
                            }
                          }}
                          disabled={planSavingUserId === user._id}
                          autoFocus
                        >
                          {plans.length > 0
                            ? plans.map(plan => (
                                <option key={plan.name} value={plan.name}>
                                  {plan.label || plan.name}
                                </option>
                              ))
                            : (
                                <option value={planEditValue}>
                                  {planLabelMap[planEditValue] || planEditValue || 'free'}
                                </option>
                              )}
                        </select>
                        {planSavingUserId === user._id && (
                          <span className="plan-edit-saving">Updating...</span>
                        )}
                      </div>
                    ) : (
                      <span
                        className="status-badge plan-badge"
                        role="button"
                        tabIndex={0}
                        onClick={() => handlePlanBadgeClick(user)}
                        onKeyDown={(event) => handlePlanBadgeKeyDown(event, user)}
                        title="Click to change plan"
                      >
                        {planLabelMap[user.plan] || user.plan || 'free'}
                      </span>
                      )}
                    </td>
                    <td>
                      {user.stripeSubscriptionId ? (
                        <button
                          className="subscription-date-button"
                          onClick={() => handleCancelSubscription(user)}
                          disabled={cancelingUserId === user._id}
                          title="Click to schedule cancellation at the period end"
                        >
                          <span>{formatSubscriptionDate(user.subscriptionCurrentPeriodEnd)}</span>
                          {user.subscriptionStatus && (
                            <span className="subscription-date-status">
                              {user.subscriptionStatus.replace(/_/g, ' ')}
                            </span>
                          )}
                          {user.subscriptionCancelAtPeriodEnd && (
                            <span className="subscription-date-status">Cancellation scheduled</span>
                          )}
                          {cancelingUserId === user._id && (
                            <span className="subscription-date-status">Cancelling…</span>
                          )}
                        </button>
                      ) : (
                        <span className="subscription-none">No active subscription</span>
                      )}
                    </td>
                    <td>
                      {(() => {
                        // Show first connected site URL instead of profile websiteUrl
                        const urlToShow = (user.sites && user.sites.length > 0 ? user.sites[0].url : null) || user.websiteUrl;
                        return urlToShow ? (
                    <a 
                            href={urlToShow} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="website-link"
                    >
                            {urlToShow}
                    </a>
                  ) : (
                    <span className="no-url">No URL provided</span>
                        );
                      })()}
                </td>
                <td>{getStatusBadge(user.status)}</td>
                <td>{formatDate(user.createdAt)}</td>
                <td>
                  <div className="action-buttons">
                    {user.status === 'pending' && settings.requireUserApproval && (
                      <>
                        <button
                          onClick={() => handleApprove(user._id)}
                          className="btn-approve"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => {
                            setSelectedUser(user);
                            setShowRejectModal(true);
                          }}
                          className="btn-reject"
                        >
                          Reject
                        </button>
                      </>
                    )}
                        {!user.isAdmin && (
                          <button
                            onClick={() => handlePromote(user._id, 'promote')}
                            className="btn-promote"
                            style={{ background: '#8b5cf6', color: 'white' }}
                          >
                            Make Admin
                          </button>
                    )}
                    <button
                      onClick={() => handleDelete(user._id)}
                      className="btn-delete"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="pagination">
          <button
            onClick={() => fetchUsers(pagination.page - 1)}
            disabled={pagination.page === 1}
            className="pagination-btn"
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {pagination.page} of {pagination.pages}
          </span>
          <button
            onClick={() => fetchUsers(pagination.page + 1)}
            disabled={pagination.page === pagination.pages}
            className="pagination-btn"
          >
            Next
          </button>
            </div>
          )}
        </>
      )}

      {activeTab === 'plans' && (
        <div className="plans-tab">
          <div className="plans-main">
            <div className="plan-card">
              <h2>Subscription Settings</h2>
              <p className="plan-description">
                Configure retention and refresh policies for each plan. Users default to the free tier.
              </p>
              {planError && <div className="plan-error">{planError}</div>}
              {plansLoading && plans.length === 0 ? (
                <div className="loading">Loading plans...</div>
              ) : (
                <>
                  {plans.length > 1 && (
                    <div className="plan-field">
                      <label htmlFor="plan-select">Select Plan</label>
                      <select
                        id="plan-select"
                        value={selectedPlanName}
                        onChange={(e) => handlePlanSelect(e.target.value)}
                      >
                        {plans.map(plan => (
                          <option key={plan.name} value={plan.name}>
                            {plan.label || plan.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="plan-form-grid">
                    <div className="plan-field">
                      <label htmlFor="plan-label">Label</label>
                      <input
                        id="plan-label"
                        type="text"
                        value={planForm.label}
                        onChange={(e) => handlePlanInputChange('label', e.target.value)}
                      />
                    </div>
                    <div className="plan-field">
                      <label htmlFor="plan-retention">Data Retention (days)</label>
                      <input
                        id="plan-retention"
                        type="number"
                        min="1"
                        value={planForm.dataRetentionDays}
                        onChange={(e) => handlePlanInputChange('dataRetentionDays', e.target.value)}
                      />
                    </div>
                    <div className="plan-field">
                      <label htmlFor="plan-refresh">Min Refresh Interval (hours)</label>
                      <input
                        id="plan-refresh"
                        type="number"
                        min="0"
                        step="0.25"
                        value={planForm.minRefreshIntervalHours}
                        onChange={(e) => handlePlanInputChange('minRefreshIntervalHours', e.target.value)}
                      />
                    </div>
                    <div className="plan-field">
                      <label htmlFor="plan-monthly-price">Monthly Price ($)</label>
                      <input
                        id="plan-monthly-price"
                        type="number"
                        min="0"
                        step="0.01"
                        value={planForm.monthlyPrice}
                        onChange={(e) => handlePlanInputChange('monthlyPrice', e.target.value)}
                      />
                    </div>
                    <div className="plan-field">
                      <label htmlFor="plan-yearly-price">Yearly Price ($)</label>
                      <input
                        id="plan-yearly-price"
                        type="number"
                        min="0"
                        step="0.01"
                        value={planForm.yearlyPrice}
                        onChange={(e) => handlePlanInputChange('yearlyPrice', e.target.value)}
                      />
                    </div>
                    <div className="plan-field">
                      <label htmlFor="plan-max-sites">Included Sites</label>
                      <input
                        id="plan-max-sites"
                        type="number"
                        min="0"
                        value={planForm.maxSites}
                        onChange={(e) => handlePlanInputChange('maxSites', e.target.value)}
                      />
                    </div>
                    <div className="plan-field">
                      <label htmlFor="plan-currency">Currency</label>
                      <select
                        id="plan-currency"
                        value={planForm.currency}
                        onChange={(e) => handlePlanInputChange('currency', e.target.value)}
                      >
                        <option value="usd">USD</option>
                      </select>
                    </div>
                    <div className="plan-field plan-field-full">
                      <label htmlFor="plan-description">Description</label>
                      <textarea
                        id="plan-description"
                        rows="3"
                        value={planForm.description}
                        onChange={(e) => handlePlanInputChange('description', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="plan-meta">
                    <div className="plan-meta-content">
                      <div className="plan-meta-column">
                        <div className="plan-meta-label">Included Sites</div>
                        <div className="plan-meta-values">
                          <span>
                            {planForm.maxSites >= 999 ? 'Unlimited' : planForm.maxSites ?? '—'}
                          </span>
                        </div>
                      </div>
                      <div className="plan-meta-column">
                        <div className="plan-meta-label">Stripe Product</div>
                        <div className="plan-meta-values">
                          <span>{planForm.stripeProductId || 'Not synced yet'}</span>
                        </div>
                      </div>
                      <div className="plan-meta-column">
                        <div className="plan-meta-label">Monthly Price ID</div>
                        <div className="plan-meta-values">
                          <span>{planForm.stripeMonthlyPriceId || '—'}</span>
                        </div>
                      </div>
                      <div className="plan-meta-column">
                        <div className="plan-meta-label">Yearly Price ID</div>
                        <div className="plan-meta-values">
                          <span>{planForm.stripeYearlyPriceId || '—'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  {planSyncStatus && (
                    <div className={`plan-sync-status ${planSyncStatus.type}`}>
                      {planSyncStatus.message}
                    </div>
                  )}
                  <div className="plan-actions">
                    <button
                      className="btn-save-plan"
                      onClick={handlePlanSave}
                      disabled={plansLoading}
                    >
                      {plansLoading ? 'Saving...' : 'Save Plan'}
                    </button>
                    <button
                      className="btn-sync-plan"
                      onClick={handlePlanSync}
                      disabled={planSyncing}
                    >
                      {planSyncing ? 'Syncing…' : 'Sync with Stripe'}
                    </button>
                  </div>
                </>
              )}
            </div>
            <div className="plan-stats-card">
              <h3>User Distribution</h3>
              {planCountsEntries.length === 0 ? (
                <p className="plan-stats-empty">No users assigned yet.</p>
              ) : (
                <ul>
                  {planCountsEntries.map(({ name, label, count }) => (
                    <li key={name}>
                      <span>{label}</span>
                      <span>{count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'revvy' && (
        <RevvyAdminTab />
      )}

      {/* Admins Tab Content */}
      {activeTab === 'admins' && (
        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Admin Since</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {admins.map((admin) => (
                <tr key={admin._id}>
                  <td>
                    <div className="user-info">
                      <div className="user-name">
                        {admin.firstName} {admin.lastName}
                      </div>
                    </div>
                  </td>
                  <td>{admin.email}</td>
                  <td>{formatDate(admin.approvedAt || admin.createdAt)}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => handlePromote(admin._id, 'demote')}
                        className="btn-reject"
                      >
                        Remove Admin
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Settings Tab Content */}
      {activeTab === 'settings' && (
        <div className="settings-container">
          <div className="settings-card">
            <h2>System Settings</h2>
            <p className="settings-description">Configure how user registration and access works.</p>
            
            <div className="setting-item">
              <div className="setting-info">
                <label className="setting-label">Require User Approval</label>
                <p className="setting-description">
                  When enabled, all new users must be manually approved by an admin before they can access the system.
                </p>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.requireUserApproval}
                  onChange={(e) => handleSettingChange('requireUserApproval', e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <label className="setting-label">Require Email Verification</label>
                <p className="setting-description">
                  When enabled, users must verify their email address before they can log in.
                </p>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.emailVerificationRequired}
                  onChange={(e) => handleSettingChange('emailVerificationRequired', e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <button
              onClick={handleSettingsSave}
              disabled={settingsLoading}
              className="settings-save-btn"
              style={{
                background: '#10b981',
                color: 'white',
                border: 'none',
                padding: '0.75rem 2rem',
                borderRadius: '8px',
                fontWeight: '600',
                cursor: settingsLoading ? 'not-allowed' : 'pointer',
                opacity: settingsLoading ? 0.6 : 1
              }}
            >
              {settingsLoading ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      )}

      {/* API Testing Tab */}
      {activeTab === 'api-test' && (
        <div className="api-test-section">
          <div className="section-header">
            <h2>API Testing & Data Verification</h2>
            <p>Test GA4 and AdSense API calls and inspect raw data with calculated values</p>
          </div>
          
          {/* Sub-tabs for GA4 vs AdSense */}
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', borderBottom: '2px solid #e5e7eb' }}>
            <button
              data-ga4-tab
              onClick={() => {
                const ga4Section = document.getElementById('ga4-api-section');
                const adsenseSection = document.getElementById('adsense-api-section');
                if (ga4Section) ga4Section.style.display = 'block';
                if (adsenseSection) adsenseSection.style.display = 'none';
                const ga4Btn = document.querySelector('[data-ga4-tab]');
                const adsenseBtn = document.querySelector('[data-adsense-tab]');
                if (ga4Btn) {
                  ga4Btn.style.borderBottomColor = '#3b82f6';
                  ga4Btn.style.color = '#3b82f6';
                }
                if (adsenseBtn) {
                  adsenseBtn.style.borderBottomColor = 'transparent';
                  adsenseBtn.style.color = '#6b7280';
                }
              }}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'transparent',
                border: 'none',
                borderBottom: '2px solid #3b82f6',
                color: '#3b82f6',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              GA4 API Testing
            </button>
            <button
              data-adsense-tab
              onClick={() => {
                const ga4Section = document.getElementById('ga4-api-section');
                const adsenseSection = document.getElementById('adsense-api-section');
                if (ga4Section) ga4Section.style.display = 'none';
                if (adsenseSection) adsenseSection.style.display = 'block';
                const ga4Btn = document.querySelector('[data-ga4-tab]');
                const adsenseBtn = document.querySelector('[data-adsense-tab]');
                if (adsenseBtn) {
                  adsenseBtn.style.borderBottomColor = '#3b82f6';
                  adsenseBtn.style.color = '#3b82f6';
                }
                if (ga4Btn) {
                  ga4Btn.style.borderBottomColor = 'transparent';
                  ga4Btn.style.color = '#6b7280';
                }
              }}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'transparent',
                border: 'none',
                borderBottom: '2px solid transparent',
                color: '#6b7280',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              AdSense API Testing
            </button>
          </div>
          
          <div id="ga4-api-section">

          <div className="api-controls">
            <div className="form-group">
              <label>Select Site:</label>
              <select
                value={selectedSite?.siteId || ''}
                onChange={(e) => {
                  const site = sites.find(s => s.siteId === e.target.value);
                  setSelectedSite(site);
                }}
                className="site-select"
              >
                {sites.map(site => (
                  <option key={site.siteId} value={site.siteId}>
                    {site.name} ({site.url})
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={testGA4API}
              disabled={apiLoading || !selectedSite}
              className="test-api-btn"
              style={{
                background: '#10b981',
                color: 'white',
                border: 'none',
                padding: '0.75rem 2rem',
                borderRadius: '8px',
                fontWeight: '600',
                cursor: apiLoading ? 'not-allowed' : 'pointer',
                opacity: apiLoading ? 0.6 : 1
              }}
            >
              {apiLoading ? 'Testing...' : 'Test GA4 Overview API'}
            </button>
            
            <button
              onClick={async () => {
                if (!selectedSite) return;
                setApiLoading(true);
                try {
                  const response = await axios.post(`/api/analytics-data/${selectedSite.siteId}/sync?days=30`);
                  console.log('Sync complete:', response.data);
                  alert('Sync complete! Data updated.');
                  // Refresh the API test data
                  await testGA4API();
                } catch (error) {
                  console.error('Error syncing data:', error);
                  alert('Error syncing data: ' + (error.response?.data?.message || 'Unknown error'));
                } finally {
                  setApiLoading(false);
                }
              }}
              disabled={apiLoading || !selectedSite}
              className="test-api-btn"
              style={{
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                padding: '0.75rem 2rem',
                borderRadius: '8px',
                fontWeight: '600',
                cursor: apiLoading ? 'not-allowed' : 'pointer',
                opacity: apiLoading ? 0.6 : 1,
                marginLeft: '0.5rem'
              }}
            >
              {apiLoading ? 'Syncing...' : 'Sync GA4 Data'}
            </button>
          </div>

          {(baseApiResult || apiResult) && (
            <div className="api-results">
              <h3>API Results</h3>
              
              {(apiResult?.error || baseApiResult?.error) ? (
                <div className="error-box">{(apiResult?.error || baseApiResult?.error)}</div>
              ) : (
                <div className="results-grid">
                  {/* Summary Data - Base Data (All Devices) */}
                  <div className="result-section full-width">
                    <h4>Base Data (All Devices, All Traffic Sources)</h4>
                    <div className="data-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Metric</th>
                            <th>Value</th>
                            <th>Formula</th>
                          </tr>
                        </thead>
                        <tbody>
                          {baseApiResult && baseApiResult.summary && getAllMetricsWithSubmetrics(baseApiResult.summary, baseApiResult.deviceBreakdown, { device: 'all', trafficSource: 'all' }).map((metric) => (
                            <tr key={metric.key}>
                              <td>{metric.label}</td>
                              <td>{metric.value}</td>
                              <td className="formula-cell">{metric.formula}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Filter Results */}
                  <div className="result-section full-width">
                    <h4>Filtered Data</h4>
                    <div className="filter-selectors">
                      <div className="filter-group">
                        <label>Device:</label>
                        <select
                          value={selectedFilters.device}
                          onChange={(e) => handleApiFilterChange('device', e.target.value)}
                          className="filter-select"
                        >
                          <option value="all">All Devices</option>
                          <option value="mobile">Mobile</option>
                          <option value="desktop">Desktop</option>
                          <option value="tablet">Tablet</option>
                        </select>
                        <div style={{ marginTop: '0.75rem', width: '100%' }}>
                          <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 600, color: '#374151' }}>Geo (Country):</label>
                          <select
                            value={selectedFilters.geo || 'all'}
                            onChange={(e) => handleApiFilterChange('geo', e.target.value)}
                            className="filter-select"
                            style={{ minWidth: '220px', width: '100%' }}
                          >
                            <option value="all">All Countries</option>
                            {availableGeos.map(geo => (
                              <option key={geo} value={geo}>{geo}</option>
                            ))}
                            {availableGeos.length === 0 && (
                              <option value="" disabled>No geo data available. Run test first.</option>
                            )}
                          </select>
                        </div>
                      </div>
                      
                      <div className="filter-group">
                        <label>Web Traffic:</label>
                        <div className="filter-checkboxes">
                          <label><input type="checkbox" checked={selectedFilters.webTrafficSources.includes('direct')} onChange={(e) => {
                            const sources = e.target.checked 
                              ? [...selectedFilters.webTrafficSources, 'direct']
                              : selectedFilters.webTrafficSources.filter(s => s !== 'direct');
                            handleApiFilterChange('webTrafficSources', sources);
                          }} /> Direct</label>
                          <label><input type="checkbox" checked={selectedFilters.webTrafficSources.includes('organic')} onChange={(e) => {
                            const sources = e.target.checked 
                              ? [...selectedFilters.webTrafficSources, 'organic']
                              : selectedFilters.webTrafficSources.filter(s => s !== 'organic');
                            handleApiFilterChange('webTrafficSources', sources);
                          }} /> Organic</label>
                          <label><input type="checkbox" checked={selectedFilters.webTrafficSources.includes('paid')} onChange={(e) => {
                            const sources = e.target.checked 
                              ? [...selectedFilters.webTrafficSources, 'paid']
                              : selectedFilters.webTrafficSources.filter(s => s !== 'paid');
                            handleApiFilterChange('webTrafficSources', sources);
                          }} /> Paid</label>
                          <label><input type="checkbox" checked={selectedFilters.webTrafficSources.includes('referral')} onChange={(e) => {
                            const sources = e.target.checked 
                              ? [...selectedFilters.webTrafficSources, 'referral']
                              : selectedFilters.webTrafficSources.filter(s => s !== 'referral');
                            handleApiFilterChange('webTrafficSources', sources);
                          }} /> Referral</label>
                        </div>
                        <div style={{ marginTop: '0.75rem' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: 600, color: '#374151' }}>
                            <input 
                              type="checkbox" 
                              checked={selectedFilters.emailSelected} 
                              onChange={(e) => handleApiFilterChange('emailSelected', e.target.checked)} 
                            /> Email
                          </label>
                        </div>
                      </div>
                      
                      <div className="filter-group">
                        <label>Social:</label>
                        <div className="filter-checkboxes">
                          <label><input type="checkbox" checked={selectedFilters.socialSources.includes('facebook')} onChange={(e) => {
                            const sources = e.target.checked 
                              ? [...selectedFilters.socialSources, 'facebook']
                              : selectedFilters.socialSources.filter(s => s !== 'facebook');
                            handleApiFilterChange('socialSources', sources);
                          }} /> Facebook</label>
                          <label><input type="checkbox" checked={selectedFilters.socialSources.includes('twitter')} onChange={(e) => {
                            const sources = e.target.checked 
                              ? [...selectedFilters.socialSources, 'twitter']
                              : selectedFilters.socialSources.filter(s => s !== 'twitter');
                            handleApiFilterChange('socialSources', sources);
                          }} /> Twitter/X</label>
                          <label><input type="checkbox" checked={selectedFilters.socialSources.includes('reddit')} onChange={(e) => {
                            const sources = e.target.checked 
                              ? [...selectedFilters.socialSources, 'reddit']
                              : selectedFilters.socialSources.filter(s => s !== 'reddit');
                            handleApiFilterChange('socialSources', sources);
                          }} /> Reddit</label>
                          <label><input type="checkbox" checked={selectedFilters.socialSources.includes('youtube')} onChange={(e) => {
                            const sources = e.target.checked 
                              ? [...selectedFilters.socialSources, 'youtube']
                              : selectedFilters.socialSources.filter(s => s !== 'youtube');
                            handleApiFilterChange('socialSources', sources);
                          }} /> YouTube</label>
                          <label><input type="checkbox" checked={selectedFilters.socialSources.includes('tiktok')} onChange={(e) => {
                            const sources = e.target.checked 
                              ? [...selectedFilters.socialSources, 'tiktok']
                              : selectedFilters.socialSources.filter(s => s !== 'tiktok');
                            handleApiFilterChange('socialSources', sources);
                          }} /> TikTok</label>
                          <label><input type="checkbox" checked={selectedFilters.socialSources.includes('instagram')} onChange={(e) => {
                            const sources = e.target.checked 
                              ? [...selectedFilters.socialSources, 'instagram']
                              : selectedFilters.socialSources.filter(s => s !== 'instagram');
                            handleApiFilterChange('socialSources', sources);
                          }} /> Instagram</label>
                          <label><input type="checkbox" checked={selectedFilters.socialSources.includes('linkedin')} onChange={(e) => {
                            const sources = e.target.checked 
                              ? [...selectedFilters.socialSources, 'linkedin']
                              : selectedFilters.socialSources.filter(s => s !== 'linkedin');
                            handleApiFilterChange('socialSources', sources);
                          }} /> LinkedIn</label>
                          <label><input type="checkbox" checked={selectedFilters.socialSources.includes('bluesky')} onChange={(e) => {
                            const sources = e.target.checked 
                              ? [...selectedFilters.socialSources, 'bluesky']
                              : selectedFilters.socialSources.filter(s => s !== 'bluesky');
                            handleApiFilterChange('socialSources', sources);
                          }} /> Bluesky</label>
                        </div>
                      </div>
                      
                      <div className="filter-group">
                        <label>AI:</label>
                        <div className="filter-checkboxes">
                          <label><input type="checkbox" checked={selectedFilters.aiSources.includes('chatgpt')} onChange={(e) => {
                            const sources = e.target.checked 
                              ? [...selectedFilters.aiSources, 'chatgpt']
                              : selectedFilters.aiSources.filter(s => s !== 'chatgpt');
                            handleApiFilterChange('aiSources', sources);
                          }} /> ChatGPT</label>
                          <label><input type="checkbox" checked={selectedFilters.aiSources.includes('claude')} onChange={(e) => {
                            const sources = e.target.checked 
                              ? [...selectedFilters.aiSources, 'claude']
                              : selectedFilters.aiSources.filter(s => s !== 'claude');
                            handleApiFilterChange('aiSources', sources);
                          }} /> Claude</label>
                          <label><input type="checkbox" checked={selectedFilters.aiSources.includes('perplexity')} onChange={(e) => {
                            const sources = e.target.checked 
                              ? [...selectedFilters.aiSources, 'perplexity']
                              : selectedFilters.aiSources.filter(s => s !== 'perplexity');
                            handleApiFilterChange('aiSources', sources);
                          }} /> Perplexity</label>
                          <label><input type="checkbox" checked={selectedFilters.aiSources.includes('gemini')} onChange={(e) => {
                            const sources = e.target.checked 
                              ? [...selectedFilters.aiSources, 'gemini']
                              : selectedFilters.aiSources.filter(s => s !== 'gemini');
                            handleApiFilterChange('aiSources', sources);
                          }} /> Gemini</label>
                          <label><input type="checkbox" checked={selectedFilters.aiSources.includes('bing')} onChange={(e) => {
                            const sources = e.target.checked 
                              ? [...selectedFilters.aiSources, 'bing']
                              : selectedFilters.aiSources.filter(s => s !== 'bing');
                            handleApiFilterChange('aiSources', sources);
                          }} /> Bing Chat</label>
                          <label><input type="checkbox" checked={selectedFilters.aiSources.includes('poe')} onChange={(e) => {
                            const sources = e.target.checked 
                              ? [...selectedFilters.aiSources, 'poe']
                              : selectedFilters.aiSources.filter(s => s !== 'poe');
                            handleApiFilterChange('aiSources', sources);
                          }} /> Poe</label>
                          <label><input type="checkbox" checked={selectedFilters.aiSources.includes('characterai')} onChange={(e) => {
                            const sources = e.target.checked 
                              ? [...selectedFilters.aiSources, 'characterai']
                              : selectedFilters.aiSources.filter(s => s !== 'characterai');
                            handleApiFilterChange('aiSources', sources);
                          }} /> Character.AI</label>
                          <label><input type="checkbox" checked={selectedFilters.aiSources.includes('huggingface')} onChange={(e) => {
                            const sources = e.target.checked 
                              ? [...selectedFilters.aiSources, 'huggingface']
                              : selectedFilters.aiSources.filter(s => s !== 'huggingface');
                            handleApiFilterChange('aiSources', sources);
                          }} /> Hugging Face</label>
                        </div>
                      </div>
                    </div>
                    
                    {(apiResult?.summary || baseApiResult?.summary) && (() => {
                      // Determine which data to use: prioritize apiResult if any filters are active
                      const hasActiveFilters = 
                        selectedFilters.device !== 'all' ||
                        selectedFilters.webTrafficSources.length > 0 ||
                        selectedFilters.socialSources.length > 0 ||
                        selectedFilters.aiSources.length > 0 ||
                        selectedFilters.emailSelected ||
                        (selectedFilters.geo && selectedFilters.geo !== 'all');
                      
                      const dataToUse = (hasActiveFilters && apiResult?.summary) ? apiResult : baseApiResult;
                      
                      console.log('📊 Displaying filtered data:', {
                        hasActiveFilters,
                        hasApiResult: !!apiResult?.summary,
                        hasBaseResult: !!baseApiResult?.summary,
                        usingData: dataToUse === apiResult ? 'apiResult' : 'baseApiResult',
                        filters: selectedFilters
                      });
                      
                      // Collect all active traffic sources for display
                      const allActiveSources = [];
                      if (selectedFilters.webTrafficSources.length > 0) {
                        allActiveSources.push(...selectedFilters.webTrafficSources);
                      }
                      if (selectedFilters.socialSources.length > 0) {
                        allActiveSources.push(...selectedFilters.socialSources);
                      }
                      if (selectedFilters.aiSources.length > 0) {
                        allActiveSources.push(...selectedFilters.aiSources);
                      }
                      if (selectedFilters.emailSelected) {
                        allActiveSources.push('email');
                      }
                      
                      // For display purposes, show "multiple" if more than one source
                      let displayTrafficSource = 'all';
                      if (allActiveSources.length === 1) {
                        displayTrafficSource = allActiveSources[0];
                      } else if (allActiveSources.length > 1) {
                        displayTrafficSource = 'multiple';
                      }
                      
                      return (
                        <div className="data-table" style={{ marginTop: '1.5rem' }}>
                          <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#f3f4f6', borderRadius: '0.5rem', fontSize: '0.875rem' }}>
                            <strong>Active Filters:</strong> {' '}
                            {displayTrafficSource !== 'all' && <span>Traffic: {displayTrafficSource} </span>}
                            {selectedFilters.device !== 'all' && <span>Device: {selectedFilters.device} </span>}
                            {selectedFilters.geo && selectedFilters.geo !== 'all' && <span>Geo: {selectedFilters.geo}</span>}
                            {!hasActiveFilters && <span>No filters active (showing base data)</span>}
                          </div>
                          <table>
                            <thead>
                              <tr>
                                <th>Metric</th>
                                <th>Value</th>
                                <th>Formula</th>
                              </tr>
                            </thead>
                            <tbody>
              {getAllMetricsWithSubmetrics(dataToUse.summary, dataToUse.deviceBreakdown, {
                device: selectedFilters.device,
                trafficSource: displayTrafficSource,
                geo: selectedFilters.geo && selectedFilters.geo !== 'all' ? selectedFilters.geo : 'all'
              }).map((metric) => (
                                <tr key={metric.key}>
                                  <td>{metric.label}</td>
                                  <td>{metric.value}</td>
                                  <td className="formula-cell">{metric.formula}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Raw Response */}
                  <div className="result-section full-width">
                    <h4>Raw API Response</h4>
                    <pre className="json-display">
                      {JSON.stringify(apiResult || baseApiResult, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          )}
          </div>
          
          <div id="adsense-api-section" style={{ display: 'none' }}>
            <div className="adsense-api-section">
              <div className="section-header">
                <h3>AdSense API Testing</h3>
                <p>Test AdSense API calls and inspect revenue data</p>
              </div>

              <div className="api-controls">
                <div className="form-group">
                  <label>Select Site:</label>
                  <select
                    value={selectedSite?.siteId || ''}
                    onChange={(e) => {
                      const site = sites.find(s => s.siteId === e.target.value);
                      setSelectedSite(site);
                    }}
                    className="site-select"
                  >
                    {sites.map(site => (
                      <option key={site.siteId} value={site.siteId}>
                        {site.name} ({site.url})
                      </option>
                    ))}
                  </select>
                </div>

                <button
                  onClick={async () => {
                    if (!selectedSite) {
                      setError('Please select a site');
                      return;
                    }
                    
                    setAdsenseApiLoading(true);
                    try {
                      const response = await axios.post('/api/adsense/accounts', {
                        accessToken: 'test', // Will use user's token from auth
                        siteId: selectedSite.siteId
                      }, {
                        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                      });
                      
                      setAdsenseApiResult({ accounts: response.data.accounts || [] });
                    } catch (error) {
                      console.error('Error fetching AdSense accounts:', error);
                      setAdsenseApiResult({ error: error.response?.data?.message || 'Failed to fetch AdSense accounts' });
                    } finally {
                      setAdsenseApiLoading(false);
                    }
                  }}
                  disabled={adsenseApiLoading || !selectedSite}
                  className="test-api-btn"
                  style={{
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 2rem',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: adsenseApiLoading ? 'not-allowed' : 'pointer',
                    opacity: adsenseApiLoading ? 0.6 : 1
                  }}
                >
                  {adsenseApiLoading ? 'Testing...' : 'Test AdSense Accounts API'}
                </button>

                <button
                  onClick={async () => {
                    if (!selectedSite) {
                      setError('Please select a site');
                      return;
                    }
                    
                    setAdsenseApiLoading(true);
                    try {
                      const response = await axios.get('/api/adsense/data', {
                        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                        params: { siteId: selectedSite.siteId }
                      });
                      
                      setAdsenseApiResult(response.data);
                    } catch (error) {
                      console.error('Error fetching AdSense data:', error);
                      setAdsenseApiResult({ error: error.response?.data?.message || 'Failed to fetch AdSense data' });
                    } finally {
                      setAdsenseApiLoading(false);
                    }
                  }}
                  disabled={adsenseApiLoading || !selectedSite}
                  className="test-api-btn"
                  style={{
                    background: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 2rem',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: adsenseApiLoading ? 'not-allowed' : 'pointer',
                    opacity: adsenseApiLoading ? 0.6 : 1,
                    marginLeft: '0.5rem'
                  }}
                >
                  {adsenseApiLoading ? 'Testing...' : 'Test AdSense Data API'}
                </button>
              </div>

              {adsenseApiResult && (
                <div className="api-results" style={{ marginTop: '2rem' }}>
                  <h3>AdSense API Results</h3>
                  
                  {adsenseApiResult.error ? (
                    <div className="error-box">{adsenseApiResult.error}</div>
                  ) : (
                    <div className="results-grid">
                      {adsenseApiResult.accounts && (
                        <div className="result-section full-width">
                          <h4>AdSense Accounts</h4>
                          <div className="data-table">
                            <table>
                              <thead>
                                <tr>
                                  <th>Account ID</th>
                                  <th>Account Name</th>
                                  <th>Sites</th>
                                </tr>
                              </thead>
                              <tbody>
                                {adsenseApiResult.accounts.map((account, idx) => (
                                  <tr key={idx}>
                                    <td>{account.accountId}</td>
                                    <td>{account.accountName}</td>
                                    <td>{account.sites?.length || 0} sites</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* AdSense Data Table */}
                      {(adsenseApiResult.revenue !== undefined || adsenseApiResult.data) && (
                        <div className="result-section full-width">
                          <h4>AdSense Revenue Data</h4>
                          
                          {/* Summary Metrics */}
                          <div className="data-table" style={{ marginBottom: '2rem' }}>
                            <table>
                              <thead>
                                <tr>
                                  <th>Metric</th>
                                  <th>Value</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td><strong>Total Revenue</strong></td>
                                  <td>${adsenseApiResult.revenue?.toFixed(2) || '0.00'}</td>
                                </tr>
                                <tr>
                                  <td><strong>Total Pageviews</strong></td>
                                  <td>{adsenseApiResult.pageviews?.toLocaleString() || '0'}</td>
                                </tr>
                                <tr>
                                  <td><strong>Average RPM</strong></td>
                                  <td>${adsenseApiResult.rpm?.toFixed(2) || '0.00'}</td>
                                </tr>
                                <tr>
                                  <td><strong>Revenue Per User (RPU)</strong></td>
                                  <td>${adsenseApiResult.rpu ? adsenseApiResult.rpu.toFixed(2) : (adsenseApiResult.pageviews > 0 ? (adsenseApiResult.revenue / adsenseApiResult.pageviews).toFixed(4) : '0.00')}</td>
                                </tr>
                                {adsenseApiResult.accountId && (
                                  <tr>
                                    <td><strong>Account ID</strong></td>
                                    <td>{adsenseApiResult.accountId}</td>
                                  </tr>
                                )}
                                {adsenseApiResult.accountName && (
                                  <tr>
                                    <td><strong>Account Name</strong></td>
                                    <td>{adsenseApiResult.accountName}</td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>

                          {/* Daily Data Table */}
                          {adsenseApiResult.data && adsenseApiResult.data.length > 0 && (
                            <div className="data-table">
                              <h5 style={{ marginBottom: '1rem', color: '#374151' }}>Daily Breakdown</h5>
                              <table>
                                <thead>
                                  <tr>
                                    <th>Date</th>
                                    <th>Revenue</th>
                                    <th>Pageviews</th>
                                    <th>RPM</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {adsenseApiResult.data.map((day, idx) => (
                                    <tr key={idx}>
                                      <td>{day.date || 'N/A'}</td>
                                      <td>${day.revenue?.toFixed(2) || '0.00'}</td>
                                      <td>{day.pageviews?.toLocaleString() || '0'}</td>
                                      <td>${day.rpm?.toFixed(2) || '0.00'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                          
                          {(!adsenseApiResult.data || adsenseApiResult.data.length === 0) && (
                            <div className="error-box-small" style={{ marginTop: '1rem' }}>
                              No daily data available
                            </div>
                          )}
                        </div>
                      )}

                      <div className="result-section full-width">
                        <h4>Raw API Response</h4>
                        <pre className="json-display">
                          {JSON.stringify(adsenseApiResult, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Reject User</h3>
            <p>Please provide a reason for rejecting this user:</p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter rejection reason..."
              className="rejection-reason"
              rows="4"
            />
            <div className="modal-actions">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionReason('');
                }}
                className="btn-cancel"
              >
                Cancel
              </button>
              <button
                onClick={() => handleReject(selectedUser._id)}
                disabled={!rejectionReason.trim()}
                className="btn-confirm-reject"
              >
                Reject User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;

